/**
 * The singleCard duck. Default export is the reducer
 *
 * Action Types:
 * SINGLE_CARD_FETCH_REQUESTED
 * SINGLE_CARD_FETCH_SUCCEEDED
 * SINGLE_CARD_ORIGINAL_FETCH_REQUESTED
 * SINGLE_CARD_ORIGINAL_FETCH_SUCCEEDED
 * SINGLE_CARD_VARIATION_FETCH_REQUESTED
 * SINGLE_CARD_VARIATION_FETCH_SUCCEEDED
 * SINGLE_CARD_FETCH_FAILED
 *
 * Action Creators:
 * singleCardRequest
 * singleCardFailed
 * singleCardFetched
 */
import get from 'lodash/get';

import type {
  ShareDrawerClosed,
  ShareDrawerOpened,
  SingleCardState,
  SingleCardAction,
  SingleCardRequested,
  SingleCardSucceeded,
  SingleCardOriginalRequested,
  SingleCardOriginalSucceeded,
  SingleCardVariationRequested,
  SingleCardVariationSucceeded,
  SingleCardFailed,
} from '../types/singleCard';

import type { Package } from '../types';
import { ROUTER_LOCATION_CHANGED } from './route';

/** @type {ActionType} */
export const SINGLE_CARD_FETCH_REQUESTED = 'PB/singleCard/FETCH_REQUESTED';
/** @type {ActionType} */
export const SINGLE_CARD_FETCH_SUCCEEDED = 'PB/singleCard/FETCH_SUCCEEDED';

/** @type {ActionType} */
export const SINGLE_CARD_VARIATION_FETCH_REQUESTED =
  'PB/singleCard/VARIATION_FETCH_REQUESTED';

/** @type {ActionType} */
export const SINGLE_CARD_VARIATION_FETCH_SUCCEEDED =
  'PB/singleCard/VARIATION_FETCH_SUCCEEDED';

/** @type {ActionType} */
export const SINGLE_CARD_ORIGINAL_FETCH_REQUESTED =
  'PB/singleCard/ORIGINAL_FETCH_REQUESTED';

/** @type {ActionType} */
export const SINGLE_CARD_ORIGINAL_FETCH_SUCCEEDED =
  'PB/singleCard/ORIGINAL_FETCH_SUCCEEDED';

/** @type {ActionType} */
export const SINGLE_CARD_FETCH_FAILED = 'PB/singleCard/FETCH_FAILED';

/** @type {ActionType} */
export const SHARE_DRAWER_CLOSED = 'PB/singleCard/SHARE_DRAWER_CLOSED';

/** @type {ActionType} */
export const SHARE_DRAWER_OPENED = 'PB/singleCard/SHARE_DRAWER_OPENED';

export const initialState = {
  data: {}, // current card
  originalData: {}, // original card
  selectedCardId: undefined,
  selectedVariationId: undefined,
  variations: [],
  loading: false,
  error: undefined,
  shareDrawerOpen: false,
  carouselSelected: false,
  slug: '',
};

/** @ignore */
export default function singleCard(
  state: SingleCardState = initialState,
  action: SingleCardAction,
): SingleCardState {
  const data = get(action, 'payload.singleCard', {});
  const variations = get(action, 'payload.variations', []);

  switch (action.type) {
    case ROUTER_LOCATION_CHANGED:
      let locationQuery = get(action, 'payload.query', false);
      if (!locationQuery) {
        break;
      }
      let slug = get(locationQuery, 'slug', '');
      let selectedCardId = get(locationQuery, 'card', undefined);
      let selectedVariationId = get(locationQuery, 'variation_id', undefined);
      selectedCardId =
        typeof selectedCardId === 'string'
          ? parseInt(selectedCardId, 10)
          : undefined;
      selectedVariationId =
        typeof selectedVariationId === 'string'
          ? parseInt(selectedVariationId, 10)
          : undefined;
      return { ...state, slug, selectedCardId, selectedVariationId };

    case SINGLE_CARD_FETCH_REQUESTED:
    case SINGLE_CARD_ORIGINAL_FETCH_REQUESTED:
      return { ...state, loading: true, shareDrawerOpen: false };
    case SINGLE_CARD_FETCH_SUCCEEDED:
      return { ...state, loading: false, data, originalData: data, variations };
    case SINGLE_CARD_ORIGINAL_FETCH_SUCCEEDED:
      return { ...state, loading: false, originalData: data, variations };
    case SINGLE_CARD_VARIATION_FETCH_SUCCEEDED:
      return { ...state, loading: false, data };
    case SINGLE_CARD_FETCH_FAILED:
      const error = get(action, 'payload.error', {});
      return { ...state, loading: false, error };

    case SHARE_DRAWER_OPENED:
      return { ...state, shareDrawerOpen: true };
    case SHARE_DRAWER_CLOSED:
      return { ...state, shareDrawerOpen: false };
    default:
      return state;
  }
}

/**
 * This action creator will return a SINGLE_CARD_FETCH_REQUESTED action with the cardId
 * of the singleCard to be retrieved.
 *
 * @param {number} cardId the ID of the card to request
 * @return {Action}
 * @property {string} type SINGLE_CARD_FETCH_REQUESTED
 * @property {Object} payload The payload param is passed through to the action.
 */
export function singleCardRequest(
  cardId: number,
  variationId: number,
  suiteVariationId: number,
): SingleCardRequested {
  return {
    type: SINGLE_CARD_FETCH_REQUESTED,
    payload: {
      cardId,
      variationId,
      suiteVariationId,
    },
  };
}

export function singleCardOriginalRequest(
  cardId: number,
): SingleCardOriginalRequested {
  return {
    type: SINGLE_CARD_ORIGINAL_FETCH_REQUESTED,
    payload: {
      cardId,
    },
  };
}

/**
 * This action creator will return a SINGLE_CARD_FETCH_FAILED action given an error
 * message.
 *
 * @param {string} message - A message summarizing the error. May be shown to
 *     the user (So watch yr language).
 * @return {Action}
 * @property {string} type SINGLE_CARD_FETCH_FAILED
 * @property {Object} payload The payload is an object containing the message
 *     e.g.`{ message: '' }`
 */
export function singleCardFailed(error: Object): SingleCardFailed {
  return {
    type: SINGLE_CARD_FETCH_FAILED,
    payload: { error },
  };
}

/**
 * This action creator will return a SINGLE_CARD_FETCH_SUCCEEDED action given
 * a parsed singleCard.
 *
 * @param {Object} singleCard - the requested card
 * @param {Array} variations - the alternate version of the requested card
 * @return {Action}
 * @property {string} type SINGLE_CARD_FETCH_SUCCEEDED
 * @property {Object} payload The payload is an object containing the single card
 *     e.g.`{ packages: [...] }`
 */
export function singleCardFetched(
  singleCard: Object,
  variations: Package[],
): SingleCardSucceeded {
  return {
    type: SINGLE_CARD_FETCH_SUCCEEDED,
    payload: {
      singleCard,
      variations,
    },
  };
}

export function singleCardVariationFetched(
  singleCard: Object,
): SingleCardVariationSucceeded {
  return {
    type: SINGLE_CARD_VARIATION_FETCH_SUCCEEDED,
    payload: {
      singleCard,
    },
  };
}

export function singleCardOriginalFetched(
  singleCard: Object,
  variations: Package[],
): SingleCardOriginalSucceeded {
  return {
    type: SINGLE_CARD_ORIGINAL_FETCH_SUCCEEDED,
    payload: {
      singleCard,
      variations,
    },
  };
}

export function closeShareDrawer(): ShareDrawerClosed {
  return {
    type: SHARE_DRAWER_CLOSED,
  };
}

export function openShareDrawer(): ShareDrawerOpened {
  return {
    type: SHARE_DRAWER_OPENED,
  };
}
